import {
	LATEST_APP_RELEASE_BY_PLATFORM,
	type LatestAppRelease,
	type NativeAppPlatform,
} from '../../constants/app-version.constants';

export interface ForceUpdateRequirement {
	platform: NativeAppPlatform;
	installed: string;
	latest: string;
	/** Null when either side does not report a comparable build number. */
	installedBuild: string | null;
	latestBuild: string | null;
}

/**
 * Numeric dot-separated segments, or null for anything else. Numbers are
 * accepted because Android reports `appVersionCode` as one.
 */
export function parseAppVersion(value: unknown): number[] | null {
	const raw =
		typeof value === 'number' && Number.isFinite(value) && value >= 0
			? String(value)
			: typeof value === 'string'
				? value.trim()
				: null;
	if (raw === null || raw === '') return null;

	const segments = raw.split('.');
	if (!segments.every((segment) => /^\d+$/.test(segment))) return null;
	return segments.map(Number);
}

/** Missing trailing segments count as zero, so `1.5` and `1.5.0` are equal. */
export function compareAppVersions(a: number[], b: number[]): -1 | 0 | 1 {
	const length = Math.max(a.length, b.length);
	for (let index = 0; index < length; index++) {
		const left = a[index] ?? 0;
		const right = b[index] ?? 0;
		if (left < right) return -1;
		if (left > right) return 1;
	}
	return 0;
}

const isNativeAppPlatform = (value: unknown): value is NativeAppPlatform =>
	value === 'ios' || value === 'android';

const displayBuild = (value: unknown): string | null =>
	typeof value === 'number' || typeof value === 'string' ? String(value).trim() : null;

/**
 * Decides whether the sheet must block the app: strictly below the latest
 * release blocks, equal or newer passes. Version is compared first and the
 * build number only breaks a tie — iOS resets `CFBundleVersion` on each
 * marketing version, so the pair only means anything read in that order.
 * A `buildOnly` release compares the build number alone.
 *
 * Anything ambiguous — no payload, an unknown platform, a version we cannot
 * parse — resolves to null: a mis-reporting shell must never lock a user out.
 * A missing or unparsable build is not ambiguity, it just ends the comparison
 * at the version.
 */
export function resolveForceUpdate(
	info: { platform?: unknown; appVersion?: unknown; appVersionCode?: unknown } | null | undefined,
	latestByPlatform: Record<NativeAppPlatform, LatestAppRelease> = LATEST_APP_RELEASE_BY_PLATFORM
): ForceUpdateRequirement | null {
	if (!info) return null;
	const platform = typeof info.platform === 'string' ? info.platform.toLowerCase() : info.platform;
	if (!isNativeAppPlatform(platform)) return null;

	const release = latestByPlatform[platform];
	const installedBuild = parseAppVersion(info.appVersionCode);
	const latestBuild = parseAppVersion(release?.build);
	const comparableBuilds = Boolean(installedBuild && latestBuild);

	if (release?.buildOnly) {
		if (!comparableBuilds || compareAppVersions(installedBuild!, latestBuild!) >= 0) return null;
		return {
			platform,
			installed: displayBuild(info.appVersion) ?? '',
			latest: release.version,
			installedBuild: displayBuild(info.appVersionCode),
			latestBuild: release.build,
		};
	}

	const installed = parseAppVersion(info.appVersion);
	const latest = parseAppVersion(release?.version);
	if (!installed || !latest) return null;

	const versionOrder = compareAppVersions(installed, latest);
	const outdated =
		versionOrder < 0 ||
		(versionOrder === 0 &&
			comparableBuilds &&
			compareAppVersions(installedBuild!, latestBuild!) < 0);
	if (!outdated) return null;

	return {
		platform,
		installed: String(info.appVersion).trim(),
		latest: release.version,
		installedBuild: comparableBuilds ? displayBuild(info.appVersionCode) : null,
		latestBuild: comparableBuilds ? release.build : null,
	};
}
